import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import type { AxialCoord } from '@/types/hex.types';
import { Position } from '@/ecs/Component';
import {
  UnitData,
  Stats,
  Traits,
  Owner,
  CombatState,
  UnitMesh,
} from '@/ecs/components/UnitComponents';
import {
  getUnitDefinition,
  STAR_MULTIPLIERS,
  type UnitDefinition,
} from './UnitDefinitions';
import { UnitRenderer } from './UnitRenderer';
import { HexGrid } from '@/hex/HexGrid';

/**
 * Factory for creating unit entities with all required components
 */
export class UnitFactory {
  private world: World;
  private unitRenderer: UnitRenderer;
  private hexGrid: HexGrid;

  constructor(world: World, unitRenderer: UnitRenderer, hexGrid: HexGrid) {
    this.world = world;
    this.unitRenderer = unitRenderer;
    this.hexGrid = hexGrid;
  }

  /**
   * Create a unit entity from a unit type
   */
  createUnit(
    unitType: string,
    playerId: number,
    coord: AxialCoord,
    starLevel: number = 1
  ): Entity | null {
    const definition = getUnitDefinition(unitType);
    if (!definition) {
      console.error(`Unknown unit type: ${unitType}`);
      return null;
    }

    // Check if tile is valid and available
    const tile = this.hexGrid.getTile(coord);
    if (!tile) {
      console.error(`Invalid coordinate: ${coord.q}, ${coord.r}`);
      return null;
    }

    if (tile.occupant !== null) {
      console.error(`Tile already occupied: ${coord.q}, ${coord.r}`);
      return null;
    }

    return this.createUnitFromDefinition(definition, playerId, coord, starLevel);
  }

  /**
   * Create a unit from a definition object
   */
  createUnitFromDefinition(
    definition: UnitDefinition,
    playerId: number,
    coord: AxialCoord,
    starLevel: number = 1
  ): Entity {
    const entity = this.world.createEntity();
    const statMultiplier = STAR_MULTIPLIERS[starLevel] ?? 1;

    // Add position component
    entity.addComponent(
      new Position({
        q: coord.q,
        r: coord.r,
      })
    );

    // Add unit data
    entity.addComponent(
      new UnitData({
        unitType: definition.unitType,
        name: definition.name,
        starLevel,
      })
    );

    // Add stats (scaled by star level)
    const baseStats = definition.baseStats;
    entity.addComponent(
      new Stats({
        attack: Math.floor(baseStats.attack * statMultiplier),
        defense: Math.floor(baseStats.defense * statMultiplier),
        speed: baseStats.speed, // Speed doesn't scale with stars
        abilityPower: Math.floor(baseStats.abilityPower * statMultiplier),
        maxHealth: Math.floor(baseStats.maxHealth * statMultiplier),
        currentHealth: Math.floor(baseStats.maxHealth * statMultiplier),
        attackRange: baseStats.attackRange,
        attackSpeed: baseStats.attackSpeed,
      })
    );

    // Add traits
    entity.addComponent(
      new Traits({
        traitIds: [...definition.traits],
      })
    );

    // Add owner
    entity.addComponent(
      new Owner({
        playerId,
        isOnBench: false,
        benchSlot: null,
      })
    );

    // Add combat state
    entity.addComponent(
      new CombatState({
        targetEntityId: null,
        attackCooldown: 0,
        isMoving: false,
        isDead: false,
      })
    );

    // Create and register 3D mesh
    const meshId = this.unitRenderer.createUnitMesh(
      entity.id,
      definition.meshType,
      definition.color,
      playerId,
      starLevel,
      coord,
      definition.model,
      definition.scale
    );

    entity.addComponent(
      new UnitMesh({
        meshId,
      })
    );

    // Update hex grid occupancy
    this.hexGrid.setOccupant(coord, entity.id);

    return entity;
  }

  /**
   * Create a unit on the bench
   */
  createBenchUnit(
    unitType: string,
    playerId: number,
    benchSlot: number
  ): Entity | null {
    const definition = getUnitDefinition(unitType);
    if (!definition) {
      console.error(`Unknown unit type: ${unitType}`);
      return null;
    }

    const entity = this.world.createEntity();
    const starLevel = 1;
    const statMultiplier = STAR_MULTIPLIERS[starLevel];

    // No position component for benched units
    // Add unit data
    entity.addComponent(
      new UnitData({
        unitType: definition.unitType,
        name: definition.name,
        starLevel,
      })
    );

    // Add stats
    const baseStats = definition.baseStats;
    entity.addComponent(
      new Stats({
        attack: Math.floor(baseStats.attack * statMultiplier),
        defense: Math.floor(baseStats.defense * statMultiplier),
        speed: baseStats.speed,
        abilityPower: Math.floor(baseStats.abilityPower * statMultiplier),
        maxHealth: Math.floor(baseStats.maxHealth * statMultiplier),
        currentHealth: Math.floor(baseStats.maxHealth * statMultiplier),
        attackRange: baseStats.attackRange,
        attackSpeed: baseStats.attackSpeed,
      })
    );

    // Add traits
    entity.addComponent(
      new Traits({
        traitIds: [...definition.traits],
      })
    );

    // Add owner (on bench)
    entity.addComponent(
      new Owner({
        playerId,
        isOnBench: true,
        benchSlot,
      })
    );

    // No combat state or mesh for benched units initially
    return entity;
  }

  /**
   * Move a unit from bench to board
   */
  moveToBoard(entity: Entity, coord: AxialCoord): boolean {
    const owner = entity.getComponent(Owner);
    const unitData = entity.getComponent(UnitData);

    if (!owner || !unitData) return false;
    if (!owner.isOnBench) return false;

    const definition = getUnitDefinition(unitData.unitType);
    if (!definition) return false;

    // Check tile availability
    const tile = this.hexGrid.getTile(coord);
    if (!tile || tile.occupant !== null) return false;

    // Check ownership
    if (tile.owner !== owner.playerId) return false;

    // Add position
    entity.addComponent(
      new Position({
        q: coord.q,
        r: coord.r,
      })
    );

    // Update owner state
    const newOwner = new Owner({
      playerId: owner.playerId,
      isOnBench: false,
      benchSlot: null,
    });
    entity.removeComponent(Owner);
    entity.addComponent(newOwner);

    // Add combat state
    entity.addComponent(
      new CombatState({
        targetEntityId: null,
        attackCooldown: 0,
        isMoving: false,
        isDead: false,
      })
    );

    // Create mesh
    const meshId = this.unitRenderer.createUnitMesh(
      entity.id,
      definition.meshType,
      definition.color,
      owner.playerId,
      unitData.starLevel,
      coord,
      definition.model,
      definition.scale
    );

    entity.addComponent(
      new UnitMesh({
        meshId,
      })
    );

    // Update grid
    this.hexGrid.setOccupant(coord, entity.id);

    return true;
  }

  /**
   * Move a unit from board to bench
   */
  moveToBench(entity: Entity, benchSlot: number): boolean {
    const owner = entity.getComponent(Owner);
    const position = entity.getComponent(Position);
    const unitMesh = entity.getComponent(UnitMesh);

    if (!owner || owner.isOnBench) return false;

    // Clear grid occupancy
    if (position) {
      this.hexGrid.setOccupant({ q: position.q, r: position.r }, null);
      entity.removeComponent(Position);
    }

    // Remove mesh
    if (unitMesh) {
      this.unitRenderer.removeUnitMesh(unitMesh.meshId);
      entity.removeComponent(UnitMesh);
    }

    // Remove combat state
    entity.removeComponent(CombatState);

    // Update owner
    const newOwner = new Owner({
      playerId: owner.playerId,
      isOnBench: true,
      benchSlot,
    });
    entity.removeComponent(Owner);
    entity.addComponent(newOwner);

    return true;
  }

  /**
   * Remove a unit completely
   */
  removeUnit(entity: Entity): void {
    const position = entity.getComponent(Position);
    const unitMesh = entity.getComponent(UnitMesh);

    // Clear grid
    if (position) {
      this.hexGrid.setOccupant({ q: position.q, r: position.r }, null);
    }

    // Remove mesh
    if (unitMesh) {
      this.unitRenderer.removeUnitMesh(unitMesh.meshId);
    }

    // Remove from world
    this.world.removeEntity(entity);
  }

  /**
   * Update unit position on the grid
   */
  moveUnit(entity: Entity, newCoord: AxialCoord): boolean {
    const position = entity.getComponent(Position);
    const owner = entity.getComponent(Owner);
    const unitMesh = entity.getComponent(UnitMesh);

    if (!position || !owner || owner.isOnBench) return false;

    // Check new tile
    const newTile = this.hexGrid.getTile(newCoord);
    if (!newTile) return false;

    // Allow moving to own occupied tile (swap handled elsewhere)
    if (newTile.occupant !== null && newTile.occupant !== entity.id) {
      return false;
    }

    // Clear old position
    this.hexGrid.setOccupant({ q: position.q, r: position.r }, null);

    // Update position component
    const newPosition = new Position({ q: newCoord.q, r: newCoord.r });
    entity.removeComponent(Position);
    entity.addComponent(newPosition);

    // Update grid
    this.hexGrid.setOccupant(newCoord, entity.id);

    // Update mesh position
    if (unitMesh) {
      this.unitRenderer.updatePosition(unitMesh.meshId, newCoord);
    }

    return true;
  }

  /**
   * Swap positions of two units
   */
  swapUnits(entity1: Entity, entity2: Entity): boolean {
    const pos1 = entity1.getComponent(Position);
    const pos2 = entity2.getComponent(Position);
    const mesh1 = entity1.getComponent(UnitMesh);
    const mesh2 = entity2.getComponent(UnitMesh);

    if (!pos1 || !pos2) return false;

    const coord1 = { q: pos1.q, r: pos1.r };
    const coord2 = { q: pos2.q, r: pos2.r };

    // Update position components
    entity1.removeComponent(Position);
    entity1.addComponent(new Position(coord2));

    entity2.removeComponent(Position);
    entity2.addComponent(new Position(coord1));

    // Update grid
    this.hexGrid.setOccupant(coord1, entity2.id);
    this.hexGrid.setOccupant(coord2, entity1.id);

    // Update meshes
    if (mesh1) {
      this.unitRenderer.updatePosition(mesh1.meshId, coord2);
    }
    if (mesh2) {
      this.unitRenderer.updatePosition(mesh2.meshId, coord1);
    }

    return true;
  }
}
